/**
 * Hook that provides the inventory collection as well as several convenience values:
 *   - assetTypeLabel: a method for generically referencing asset types in user messaging
 *   - selectedCount: the count of assets we're managing
 *   - onSearch: a method for filtering the collection (local fuzzy search)
 */
import { useEffect, useMemo } from 'react';
import { AssetType, getLabelTypeValue } from '@kentik/ui-shared/labels/lib';
import AssetTagInventoryCollection from 'app/stores/assetTags/AssetTagInventoryCollection';
import type { RelatedAssetCollection } from 'app/stores/assetTags/$assetTags.types';
import type { ModelBase } from 'core/model/model.types';
import useCollectionFiltering, { FilterableCollection } from './useCollectionFiltering';

type AssetModel = ModelBase & {
  isNew?: boolean;
};

interface Props {
  assetType: AssetType;
  assetCollection?: RelatedAssetCollection;
  assetModel?: AssetModel;
  onLoad?: (args: { collection: AssetTagInventoryCollection }) => void;
}

function useInventoryCollection(props: Props) {
  const { assetType, assetCollection, assetModel, onLoad } = props;
  const isNewModel = !!assetModel?.isNew;
  const selectedArray = useMemo(
    () => (assetModel ? [assetModel] : (assetCollection?.selectedArray ?? [])),
    [assetCollection?.selectedArray, assetModel]
  );

  // label based on asset type we can use for specific asset messaging
  const assetTypeLabel = useMemo(() => getLabelTypeValue(assetType).toLowerCase(), [assetType]);

  // derive stable asset ids used for fetching only the tags/values for the selected assets
  const assetIds = useMemo(() => selectedArray.map((model) => `${model.id}`), [selectedArray]);

  // create an inventory collection to house the tags/values and set up local fuzzy filtering
  const collection = useMemo(
    () => new AssetTagInventoryCollection([], { assetType, assetIds, singleMode: !!assetModel }),
    [assetType, assetIds, assetModel]
  );
  const { onSearch } = useCollectionFiltering({ collection: collection as unknown as FilterableCollection });

  useEffect(() => {
    if (!isNewModel) {
      // only skip fetching when we're dealing with a new model
      // new models won't have matching tags and their format will cause backend errors anyway
      collection.fetch({ force: true, data: { asset_ids: assetIds } });
    }

    if (onLoad) {
      onLoad({ collection });
    }
  }, [collection, assetIds, isNewModel, onLoad]);

  return { collection, onSearch, assetTypeLabel, selectedCount: selectedArray.length };
}

export default useInventoryCollection;
